/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import {Logger} from '@app/lib/Logger';
import {makePersistent} from '@app/lib/MobXPersistence';
import MediaPermissionStore from '@app/stores/MediaPermissionStore';
import VoiceConnectionManager from '@app/stores/voice/VoiceConnectionManager';
import VoiceDevicePermissionStore from '@app/stores/voice/VoiceDevicePermissionStore';
import {syncLocalVoiceStateWithServer} from '@app/stores/voice/VoiceMediaEngineBridge';
import type {VoiceDeviceState} from '@app/utils/VoiceDeviceManager';
import {makeAutoObservable, observable, reaction, runInAction} from 'mobx';

const logger = new Logger('LocalVoiceStateStore');

interface MutableLocalVoiceState {
	selfMute: boolean;
	selfDeaf: boolean;
	selfVideo: boolean;
	selfStream: boolean;
	viewerStreamKeys: Array<string>;
	hasUserSetMute: boolean;
	hasUserSetDeaf: boolean;
	mutedByPermission: boolean;
	shouldUnmuteOnUndeafen: boolean;
}

type LocalConnectionVoiceState = MutableLocalVoiceState;

class LocalVoiceStateStore implements MutableLocalVoiceState {
	selfMute = !MediaPermissionStore.isMicrophoneGranted();
	selfDeaf = false;
	selfVideo = false;
	selfStream = false;
	selfStreamAudio = false;
	selfStreamAudioMute = false;
	noiseSuppressionEnabled = true;
	viewerStreamKeys: Array<string> = [];

	hasUserSetMute = false;
	hasUserSetDeaf = false;

	shouldUnmuteOnUndeafen = false;

	private microphonePermissionGranted: boolean | null = MediaPermissionStore.isMicrophoneGranted();
	mutedByPermission = !MediaPermissionStore.isMicrophoneGranted();
	persistedSelfMute = !MediaPermissionStore.isMicrophoneGranted();
	persistedSelfDeaf = false;
	persistedHasUserSetMute = false;
	persistedHasUserSetDeaf = false;
	private persistenceHydrationPromise: Promise<void>;
	private _disposers: Array<() => void> = [];
	private lastDevicePermissionStatus: VoiceDeviceState['permissionStatus'] | null =
		VoiceDevicePermissionStore.getState().permissionStatus;
	private isNotifyingServerOfPermissionMute = false;
	private connectionStates = observable.object<Record<string, LocalConnectionVoiceState>>({});

	constructor() {
		makeAutoObservable<
			this,
			| 'microphonePermissionGranted'
			| 'mutedByPermission'
			| '_disposers'
			| 'isNotifyingServerOfPermissionMute'
			| 'shouldUnmuteOnUndeafen'
			| 'connectionStates'
			| 'createConnectionState'
			| 'getConnectionState'
			| 'getActiveConnectionId'
			| 'getActiveStateForRead'
			| 'getActiveStateForWrite'
			| 'syncPersistedAudioDefaults'
		>(
			this,
			{
				microphonePermissionGranted: false,
				mutedByPermission: false,
				_disposers: false,
				isNotifyingServerOfPermissionMute: false,
				shouldUnmuteOnUndeafen: false,
				connectionStates: false,
				createConnectionState: false,
				getConnectionState: false,
				getActiveConnectionId: false,
				getActiveStateForRead: false,
				getActiveStateForWrite: false,
				syncPersistedAudioDefaults: false,
				getSelfMute: false,
				getSelfDeaf: false,
				getSelfVideo: false,
				getSelfStream: false,
				getSelfStreamAudio: false,
				getSelfStreamAudioMute: false,
				getViewerStreamKeys: false,
				addViewerStreamKey: false,
				removeViewerStreamKey: false,
				hasViewerStreamKey: false,
				getNoiseSuppressionEnabled: false,
				getHasUserSetMute: false,
				getHasUserSetDeaf: false,
			},
			{autoBind: true},
		);
		this._disposers = [];
		this.persistenceHydrationPromise = this.initPersistence();
		this.initializePersistedDefaultSync();
		this.initializePermissionSync();
		this.initializeDevicePermissionSync();
	}

	private getPersistedAudioDefaults(): Pick<
		MutableLocalVoiceState,
		'selfMute' | 'selfDeaf' | 'hasUserSetMute' | 'hasUserSetDeaf'
	> {
		return {
			selfMute: this.persistedSelfMute,
			selfDeaf: this.persistedSelfDeaf,
			hasUserSetMute: this.persistedHasUserSetMute,
			hasUserSetDeaf: this.persistedHasUserSetDeaf,
		};
	}

	private createConnectionState(seed?: Partial<MutableLocalVoiceState>): LocalConnectionVoiceState {
		const persistedAudioDefaults = this.getPersistedAudioDefaults();
		return observable.object<LocalConnectionVoiceState>({
			selfMute: seed?.selfMute ?? persistedAudioDefaults.selfMute,
			selfDeaf: seed?.selfDeaf ?? persistedAudioDefaults.selfDeaf,
			selfVideo: seed?.selfVideo ?? this.selfVideo,
			selfStream: seed?.selfStream ?? this.selfStream,
			viewerStreamKeys: seed?.viewerStreamKeys ? [...seed.viewerStreamKeys] : [...this.viewerStreamKeys],
			hasUserSetMute: seed?.hasUserSetMute ?? persistedAudioDefaults.hasUserSetMute,
			hasUserSetDeaf: seed?.hasUserSetDeaf ?? persistedAudioDefaults.hasUserSetDeaf,
			mutedByPermission: seed?.mutedByPermission ?? this.mutedByPermission,
			shouldUnmuteOnUndeafen: seed?.shouldUnmuteOnUndeafen ?? this.shouldUnmuteOnUndeafen,
		});
	}

	private getActiveConnectionId(): string | null {
		return VoiceConnectionManager.connectionId;
	}

	private getConnectionState(connectionId: string | null): LocalConnectionVoiceState | null {
		if (!connectionId) {
			return null;
		}

		return this.connectionStates[connectionId] ?? null;
	}

	private getActiveStateForRead(): MutableLocalVoiceState {
		return this.getConnectionState(this.getActiveConnectionId()) ?? this;
	}

	private getActiveStateForWrite(): MutableLocalVoiceState {
		const connectionId = this.getActiveConnectionId();
		if (!connectionId) {
			return this;
		}

		const existingState = this.getConnectionState(connectionId);
		if (existingState) {
			return existingState;
		}

		const nextState = this.createConnectionState();
		this.connectionStates[connectionId] = nextState;
		return nextState;
	}

	private syncPersistedAudioDefaults(state: MutableLocalVoiceState): void {
		this.selfMute = state.selfMute;
		this.selfDeaf = state.selfDeaf;
		this.hasUserSetMute = state.hasUserSetMute;
		this.hasUserSetDeaf = state.hasUserSetDeaf;
		this.shouldUnmuteOnUndeafen = state.shouldUnmuteOnUndeafen;
		this.persistedSelfMute = state.selfMute;
		this.persistedSelfDeaf = state.selfDeaf;
		this.persistedHasUserSetMute = state.hasUserSetMute;
		this.persistedHasUserSetDeaf = state.hasUserSetDeaf;
	}

	private async initPersistence(): Promise<void> {
		await makePersistent(this, 'LocalVoiceStateStore', [
			'persistedSelfMute',
			'persistedSelfDeaf',
			'noiseSuppressionEnabled',
			'persistedHasUserSetMute',
			'persistedHasUserSetDeaf',
		], {
			syncAcrossTabs: true,
		});
		runInAction(() => {
			this.applyPersistedDefaultsToFallbackState();
		});
		logger.debug('LocalVoiceStateStore hydrated from localStorage on reload');
	}

	private initializePersistedDefaultSync(): void {
		const disposer = reaction(
			() => ({
				activeConnectionId: this.getActiveConnectionId(),
				selfMute: this.persistedSelfMute,
				selfDeaf: this.persistedSelfDeaf,
				hasUserSetMute: this.persistedHasUserSetMute,
				hasUserSetDeaf: this.persistedHasUserSetDeaf,
			}),
			({activeConnectionId}) => {
				if (activeConnectionId) {
					return;
				}

				runInAction(() => {
					this.applyPersistedDefaultsToFallbackState();
				});
			},
			{
				name: 'LocalVoiceStateStore-persistedDefaultSync',
			},
		);

		this._disposers.push(disposer);
	}

	private applyPersistedDefaultsToFallbackState(): void {
		const micGranted = this.microphonePermissionGranted ?? MediaPermissionStore.isMicrophoneGranted();
		this.selfMute = micGranted ? this.persistedSelfMute : true;
		this.selfDeaf = this.persistedSelfDeaf;
		this.hasUserSetMute = this.persistedHasUserSetMute;
		this.hasUserSetDeaf = this.persistedHasUserSetDeaf;
		this.mutedByPermission = !micGranted;
		this.shouldUnmuteOnUndeafen = false;
	}

	dispose(): void {
		this._disposers.forEach((disposer) => disposer());
		this._disposers = [];
	}

	private async initializePermissionSync(): Promise<void> {
		try {
			let defaultMuteInitialized = false;
			await this.persistenceHydrationPromise;

			const syncWithPermission = (source: 'init' | 'change') => {
				if (!MediaPermissionStore.isInitialized()) {
					return;
				}

				const isMicGranted = MediaPermissionStore.isMicrophoneGranted();
				const permissionState = MediaPermissionStore.getMicrophonePermissionState();

				this.microphonePermissionGranted = isMicGranted;

				logger.debug(source === 'init' ? 'Checking microphone permission for sync' : 'Microphone permission changed', {
					isMicGranted,
					permissionState,
					currentMute: this.getSelfMute(),
					hasUserSetMute: this.getHasUserSetMute(),
					mutedByPermission: this.getActiveStateForRead().mutedByPermission,
				});

				if (!isMicGranted) {
					this.applyPermissionMute();
					return;
				}

				const state = this.getActiveStateForRead();
				const shouldAutoUnmute = state.mutedByPermission && state.selfMute && !state.hasUserSetMute;
				const shouldApplyDefaultUnmute = !defaultMuteInitialized && !state.hasUserSetMute && state.selfMute;

				if (shouldAutoUnmute || shouldApplyDefaultUnmute) {
					logger.info(
						shouldAutoUnmute
							? 'Microphone permission granted, auto-unmuting after forced mute'
							: 'Microphone permission granted, defaulting to unmuted state',
						{permissionState},
					);
					runInAction(() => {
						state.selfMute = false;
						state.mutedByPermission = false;
						this.syncPersistedAudioDefaults(state);
					});
				} else {
					runInAction(() => {
						state.mutedByPermission = false;
						this.syncPersistedAudioDefaults(state);
					});
				}

				defaultMuteInitialized = true;
			};

			syncWithPermission('init');

			const disposer = MediaPermissionStore.addChangeListener(() => {
				syncWithPermission('change');
			});

			this._disposers.push(disposer);
		} catch (err) {
			logger.error('Failed to initialize permission sync', err);
		}
	}

	private initializeDevicePermissionSync(): void {
		const disposer = VoiceDevicePermissionStore.subscribe((state) => {
			this.handleDevicePermissionStatus(state.permissionStatus);
		});
		this._disposers.push(disposer);
	}

	private handleDevicePermissionStatus(status: VoiceDeviceState['permissionStatus']): void {
		if (status === this.lastDevicePermissionStatus) {
			return;
		}

		this.lastDevicePermissionStatus = status;
		if (status === 'granted') {
			void this.applyPermissionGrant();
		} else if (status === 'denied') {
			this.applyPermissionMute();
		}
	}

	private enforcePermissionMuteIfNeeded(): void {
		const devicePermission = VoiceDevicePermissionStore.getState().permissionStatus;
		const granted = MediaPermissionStore.isMicrophoneGranted() || devicePermission === 'granted';
		if (granted) {
			this.microphonePermissionGranted = true;
			return;
		}

		this.microphonePermissionGranted = false;
		this.applyPermissionMute();
	}

	private applyPermissionMute(): void {
		const shouldNotify = !this.isNotifyingServerOfPermissionMute;

		runInAction(() => {
			const state = this.getActiveStateForWrite();
			this.microphonePermissionGranted = false;
			state.mutedByPermission = true;
			if (!state.selfMute) {
				state.selfMute = true;
			}
			this.syncPersistedAudioDefaults(state);
		});

		if (shouldNotify) {
			void this.notifyServerOfPermissionMute();
		}
	}

	private async applyPermissionGrant(): Promise<void> {
		await this.persistenceHydrationPromise;
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			this.microphonePermissionGranted = true;
			if (state.mutedByPermission && state.selfMute && !state.hasUserSetMute) {
				state.selfMute = false;
			}
			state.mutedByPermission = false;
			this.syncPersistedAudioDefaults(state);
		});
	}

	private notifyServerOfPermissionMute(): void {
		if (this.isNotifyingServerOfPermissionMute) {
			logger.debug('Skipping recursive notifyServerOfPermissionMute call');
			return;
		}

		try {
			this.isNotifyingServerOfPermissionMute = true;
			syncLocalVoiceStateWithServer({self_mute: true});
		} catch (error) {
			logger.error('Failed to sync permission-mute to server', {error});
			throw error;
		} finally {
			this.isNotifyingServerOfPermissionMute = false;
		}
	}

	getSelfMute(): boolean {
		return this.getActiveStateForRead().selfMute;
	}

	ensurePermissionMute(): void {
		this.enforcePermissionMuteIfNeeded();
	}

	getSelfDeaf(): boolean {
		return this.getActiveStateForRead().selfDeaf;
	}

	getSelfVideo(): boolean {
		return this.getActiveStateForRead().selfVideo;
	}

	getSelfStream(): boolean {
		return this.getActiveStateForRead().selfStream;
	}

	getSelfStreamAudio(): boolean {
		return this.selfStreamAudio;
	}

	getSelfStreamAudioMute(): boolean {
		return this.selfStreamAudioMute;
	}

	getViewerStreamKeys(): Array<string> {
		return this.getActiveStateForRead().viewerStreamKeys;
	}

	updateViewerStreamKeys(keys: Array<string>): void {
		runInAction(() => {
			this.getActiveStateForWrite().viewerStreamKeys = keys;
		});
	}

	addViewerStreamKey(key: string): void {
		if (this.getActiveStateForRead().viewerStreamKeys.includes(key)) return;
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			state.viewerStreamKeys = [...state.viewerStreamKeys, key];
		});
	}

	removeViewerStreamKey(key: string): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			state.viewerStreamKeys = state.viewerStreamKeys.filter((k) => k !== key);
		});
	}

	hasViewerStreamKey(key: string): boolean {
		return this.getActiveStateForRead().viewerStreamKeys.includes(key);
	}

	getNoiseSuppressionEnabled(): boolean {
		return this.noiseSuppressionEnabled;
	}

	getHasUserSetMute(): boolean {
		return this.getActiveStateForRead().hasUserSetMute;
	}

	getHasUserSetDeaf(): boolean {
		return this.getActiveStateForRead().hasUserSetDeaf;
	}

	toggleSelfMute(): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			const newSelfMute = !state.selfMute;
			const micDenied = this.microphonePermissionGranted === false;

			if (state.selfDeaf && !newSelfMute) {
				state.hasUserSetMute = true;
				state.hasUserSetDeaf = true;
				state.shouldUnmuteOnUndeafen = false;

				if (micDenied) {
					state.mutedByPermission = true;
					state.selfDeaf = false;
					this.syncPersistedAudioDefaults(state);
					logger.debug('Mic denied: user attempted unmute while deaf; undeafening only');
					return;
				}

				state.selfMute = false;
				state.selfDeaf = false;
				this.syncPersistedAudioDefaults(state);
				logger.debug('User unmuted while deafened; also undeafened');
				return;
			}

			if (micDenied && !newSelfMute) {
				state.hasUserSetMute = true;
				state.mutedByPermission = true;
				this.syncPersistedAudioDefaults(state);
				logger.debug('Microphone permission denied, keeping self mute enabled despite toggle');
				return;
			}

			state.hasUserSetMute = true;
			state.selfMute = newSelfMute;

			if (!state.selfDeaf) {
				state.shouldUnmuteOnUndeafen = false;
			}

			this.syncPersistedAudioDefaults(state);
			logger.debug('User toggled self mute', {newSelfMute, hasUserSetMute: true});
		});
	}

	toggleSelfDeaf(): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			const newSelfDeaf = !state.selfDeaf;
			state.hasUserSetDeaf = true;
			const micDenied = this.microphonePermissionGranted === false;

			if (newSelfDeaf) {
				const wasMutedBefore = state.selfMute || micDenied;

				state.selfDeaf = true;
				state.selfMute = true;
				state.shouldUnmuteOnUndeafen = !wasMutedBefore;
			} else {
				state.selfDeaf = false;

				if (state.shouldUnmuteOnUndeafen && !micDenied) {
					state.selfMute = false;
				}
				state.shouldUnmuteOnUndeafen = false;
			}

			this.syncPersistedAudioDefaults(state);
			logger.debug('User toggled self deaf', {newSelfDeaf, hasUserSetDeaf: true});
		});
	}

	toggleSelfVideo(): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			state.selfVideo = !state.selfVideo;
			logger.debug('User toggled self video', {selfVideo: state.selfVideo});
		});
	}

	toggleSelfStream(): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			state.selfStream = !state.selfStream;
			logger.debug('User toggled self stream', {selfStream: state.selfStream});
		});
	}

	toggleSelfStreamAudio(): void {
		runInAction(() => {
			this.selfStreamAudio = !this.selfStreamAudio;
			logger.debug('User toggled self stream audio', {selfStreamAudio: this.selfStreamAudio});
		});
	}

	toggleSelfStreamAudioMute(): void {
		runInAction(() => {
			this.selfStreamAudioMute = !this.selfStreamAudioMute;
			logger.debug('User toggled self stream audio mute', {selfStreamAudioMute: this.selfStreamAudioMute});
		});
	}

	toggleNoiseSuppression(): void {
		runInAction(() => {
			this.noiseSuppressionEnabled = !this.noiseSuppressionEnabled;
			logger.debug('User toggled noise suppression', {enabled: this.noiseSuppressionEnabled});
		});
	}

	updateSelfMute(muted: boolean): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			if (this.microphonePermissionGranted === false && !muted) {
				state.mutedByPermission = true;
				if (!state.selfMute) {
					state.selfMute = true;
					logger.debug('Microphone permission denied, overriding requested unmute');
				}
				this.syncPersistedAudioDefaults(state);
				return;
			}

			state.selfMute = muted;
			this.syncPersistedAudioDefaults(state);
			logger.debug('Self mute updated', {muted});
		});
	}

	updateSelfDeaf(deafened: boolean): void {
		runInAction(() => {
			const state = this.getActiveStateForWrite();
			state.selfDeaf = deafened;
			if (!deafened) {
				state.shouldUnmuteOnUndeafen = false;
			}
			this.syncPersistedAudioDefaults(state);
			logger.debug('Self deaf updated', {deafened});
		});
	}

	updateSelfVideo(video: boolean): void {
		runInAction(() => {
			this.getActiveStateForWrite().selfVideo = video;
			logger.debug('Self video updated', {video});
		});
	}

	updateSelfStream(streaming: boolean): void {
		runInAction(() => {
			this.getActiveStateForWrite().selfStream = streaming;
			logger.debug('Self stream updated', {streaming});
		});
	}

	updateSelfStreamAudio(enabled: boolean): void {
		runInAction(() => {
			this.selfStreamAudio = enabled;
			logger.debug('Self stream audio updated', {enabled});
		});
	}

	updateSelfStreamAudioMute(muted: boolean): void {
		runInAction(() => {
			this.selfStreamAudioMute = muted;
			logger.debug('Self stream audio mute updated', {muted});
		});
	}

	resetUserPreferences(): void {
		runInAction(() => {
			this.hasUserSetMute = false;
			this.hasUserSetDeaf = false;
			this.selfMute = false;
			this.selfDeaf = false;
			this.persistedSelfMute = false;
			this.persistedSelfDeaf = false;
			this.persistedHasUserSetMute = false;
			this.persistedHasUserSetDeaf = false;
			this.selfVideo = false;
			this.selfStream = false;
			this.selfStreamAudio = false;
			this.selfStreamAudioMute = false;
			this.viewerStreamKeys = [];
			this.noiseSuppressionEnabled = true;
			this.mutedByPermission = false;
			this.shouldUnmuteOnUndeafen = false;
			Object.keys(this.connectionStates).forEach((connectionId) => {
				delete this.connectionStates[connectionId];
			});
		});
		if (this.microphonePermissionGranted === false) {
			logger.debug('Resetting preferences while microphone permission denied, keeping user muted');
			runInAction(() => {
				this.selfMute = true;
				this.mutedByPermission = true;
			});
		}
		logger.info('Reset user voice preferences');
	}

	seedConnectionState(
		connectionId: string,
		seed: Partial<Pick<MutableLocalVoiceState, 'selfMute' | 'selfDeaf' | 'selfVideo' | 'selfStream' | 'viewerStreamKeys'>>,
	): void {
		if (this.connectionStates[connectionId]) {
			return;
		}

		runInAction(() => {
			const state = this.createConnectionState({
				selfMute: seed.selfMute,
				selfDeaf: seed.selfDeaf,
				selfVideo: seed.selfVideo,
				selfStream: seed.selfStream,
				viewerStreamKeys: seed.viewerStreamKeys,
			});
			this.connectionStates[connectionId] = state;
			this.syncPersistedAudioDefaults(state);
		});
	}

	syncConnectionState(
		connectionId: string,
		seed: Partial<
			Pick<
				MutableLocalVoiceState,
				| 'selfMute'
				| 'selfDeaf'
				| 'selfVideo'
				| 'selfStream'
				| 'viewerStreamKeys'
				| 'hasUserSetMute'
				| 'hasUserSetDeaf'
				| 'mutedByPermission'
				| 'shouldUnmuteOnUndeafen'
			>
		>,
	): void {
		runInAction(() => {
			const state = this.connectionStates[connectionId] ?? this.createConnectionState(seed);
			if (!this.connectionStates[connectionId]) {
				this.connectionStates[connectionId] = state;
			}

			if (seed.selfMute !== undefined) {
				state.selfMute = seed.selfMute;
			}
			if (seed.selfDeaf !== undefined) {
				state.selfDeaf = seed.selfDeaf;
			}
			if (seed.selfVideo !== undefined) {
				state.selfVideo = seed.selfVideo;
			}
			if (seed.selfStream !== undefined) {
				state.selfStream = seed.selfStream;
			}
			if (seed.viewerStreamKeys !== undefined) {
				state.viewerStreamKeys = [...seed.viewerStreamKeys];
			}
			if (seed.hasUserSetMute !== undefined) {
				state.hasUserSetMute = seed.hasUserSetMute;
			}
			if (seed.hasUserSetDeaf !== undefined) {
				state.hasUserSetDeaf = seed.hasUserSetDeaf;
			}
			if (seed.mutedByPermission !== undefined) {
				state.mutedByPermission = seed.mutedByPermission;
			}
			if (seed.shouldUnmuteOnUndeafen !== undefined) {
				state.shouldUnmuteOnUndeafen = seed.shouldUnmuteOnUndeafen;
			}

			this.syncPersistedAudioDefaults(state);
		});
	}

	clearConnectionState(connectionId: string | null): void {
		if (!connectionId) {
			return;
		}

		if (!this.connectionStates[connectionId]) {
			runInAction(() => {
				this.selfVideo = false;
				this.selfStream = false;
				this.viewerStreamKeys = [];
			});
			return;
		}

		runInAction(() => {
			delete this.connectionStates[connectionId];
			this.selfVideo = false;
			this.selfStream = false;
			this.viewerStreamKeys = [];
		});
	}
}

export default new LocalVoiceStateStore();
